import { useAuth, useUser } from "@clerk/react"
import type { QueryClient } from "@tanstack/react-query"
import {
	createRootRouteWithContext,
	Outlet,
	useLocation,
	useMatches,
	useNavigate,
} from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import * as React from "react"
import { Header } from "@/components/global/header/Header"
import { AppSidebar } from "@/components/global/sidebar/app-sidebar"
import { usePagePanelOpen } from "@/components/global/sidebar/nav-model"
import { ThemeProvider } from "@/components/global/theme/theme-provider"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { Toaster } from "@/components/ui/sonner"
import { AdminRolesProvider } from "@/contexts/admin-roles-context"
import { CommandPaletteProvider } from "@/features/command-palette/CommandPaletteProvider"
import { checkAdminAccess } from "@/integrations/clerk/auth"
import ClerkProvider from "@/integrations/clerk/provider"
import { MainOrpcWsProvider } from "@/integrations/orpc/main-ws-react"
import TanStackQueryLayout from "@/integrations/tanstack-query/layout"
import { QueryProvider } from "@/integrations/tanstack-query/root-provider"
import { useRealtimeNotifications } from "@/queries/change-streams/use-realtime-notifications"
import "@fontsource-variable/geist"
import "@fontsource-variable/geist-mono"
import "@/styles.css"
import "@/lib/ag-grid"

export interface MyRouterContext {
	queryClient: QueryClient
}

declare module "@tanstack/react-router" {
	interface StaticDataRouteOption {
		/**
		 * Open without sign-in, full screen, without the sidebar and header (the
		 * spin show's TV link). The page must only read public, read-only data.
		 */
		publicPage?: boolean
	}
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	component: RootComponent,
})

function RootComponent() {
	const storageKey = "cardnexus-admin-theme"

	return (
		<ThemeProvider defaultTheme="system" storageKey={storageKey}>
			<ClerkProviderWithTheme>
				<AppLayout />
				<Toaster />
				<TanStackRouterDevtools />
				<TanStackQueryLayout />
			</ClerkProviderWithTheme>
		</ThemeProvider>
	)
}

function ClerkProviderWithTheme({ children }: { children: React.ReactNode }) {
	return (
		<ClerkProvider>
			<QueryProvider>{children}</QueryProvider>
		</ClerkProvider>
	)
}

function AppLayout() {
	const location = useLocation()
	const navigate = useNavigate()
	const { isLoaded, isSignedIn } = useAuth()
	const { user } = useUser()
	const isLoginPage = location.pathname === "/login"
	const isUnauthorizedPage = location.pathname === "/unauthorized"
	const isPublicPage = useMatches({
		select: (matches) =>
			matches.some((match) => match.staticData?.publicPage === true),
	})

	React.useEffect(() => {
		if (isLoaded && !isPublicPage) {
			if (!isSignedIn && !isLoginPage && !isUnauthorizedPage) {
				navigate({ to: "/login" })
				return
			}

			if (
				isSignedIn &&
				user &&
				!checkAdminAccess(user) &&
				!isUnauthorizedPage &&
				!isLoginPage
			) {
				navigate({ to: "/unauthorized" })
				return
			}
		}
	}, [
		isLoaded,
		isSignedIn,
		user,
		isLoginPage,
		isUnauthorizedPage,
		isPublicPage,
		navigate,
	])

	if (isPublicPage) {
		return (
			<MainOrpcWsProvider>
				<Outlet />
			</MainOrpcWsProvider>
		)
	}

	if (isLoginPage || isUnauthorizedPage) {
		return <Outlet />
	}

	if (isSignedIn && user && checkAdminAccess(user)) {
		return <AuthenticatedApp />
	}

	return null
}

function AuthenticatedApp() {
	useRealtimeNotifications()
	const [panelOpen, setPanelOpen] = usePagePanelOpen()

	return (
		<MainOrpcWsProvider>
			<AdminRolesProvider>
				<SidebarProvider open={panelOpen} onOpenChange={setPanelOpen}>
					<CommandPaletteProvider>
						<AppSidebar />
						<SidebarInset className="h-svh min-w-0">
							<Header />
							<main className="relative flex min-h-0 flex-1 flex-col">
								<div className="relative min-h-0 flex-1 overflow-y-auto">
									<Outlet />
								</div>
							</main>
						</SidebarInset>
					</CommandPaletteProvider>
				</SidebarProvider>
			</AdminRolesProvider>
		</MainOrpcWsProvider>
	)
}
